import { Accordion } from '@augmentt-dev/ui-components'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import buildApplicationRequirementOptionsMap from '../ApplicationRequirements/buildApplicationRequirementOptionsMap'
import { buildDirectoryRoleRequirementOptionsMap } from '../DirectoryRoleRequirements/useRoleFieldRequirements'
import buildGroupRequirementOptionsMap from '../GroupRequirements/buildGroupRequirementOptionsMap'
import buildNamedLocationRequirementOptionsMap from '../NamedLocationRequirements/buildNamedLocationRequirementOptionsMap'
import { buildServicePrincipalRequirementOptionsMap } from '../ServicePrincipalRequirements/useServicePrincipalFieldRequirements'
import { buildCustomAuthenticationFactorRequirementOptionsMap } from '../CustomAuthenticationFactorRequirements/useCustomAuthenticationFactorFieldRequirements'
import buildUserRequirementOptionsMap from '../UserRequirements/buildUserRequirementOptionsMap'
import ReviewTemplatePanel from '../ReviewTemplatePanel'
import getDisplayPromptedInputs from '../getDisplayPromptedInputs'
import { APPLICATION_NAMES_META_KEY } from '../../../../../util/applicationRequirement.util'

const propTypes = {
  /**
   * Selected templates
   */
  selectedTemplates: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * map of form options that are non tenant specific
   */
  globalFormOptionsMap: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

// A group/application/user/directory-role/service-principal requirement sentinel already carries its own name, so
// PrettyView's generic fallback happens to show the right thing for those on its own. A named
// location requirement's requirementId is opaque, though — its name only lives in this specific
// template's own stored `namedLocationRequirements` definitions — so, without this, a baseline's
// "view" panel shows the raw id instead of the location's name (unlike the deploy wizard and the
// template's own edit view, which already resolve it this same way).
const buildTemplateFormOptionsMap = (template, globalFormOptionsMap) => {
  const policyBody = template?.meta_data?.policyBody
  const namedLocationDefinitions = template?.meta_data?.namedLocationRequirements ?? []
  const applicationNames = template?.meta_data?.[APPLICATION_NAMES_META_KEY]

  const includeGroupsMap = buildGroupRequirementOptionsMap(policyBody?.conditions?.users?.includeGroups)
  const excludeGroupsMap = buildGroupRequirementOptionsMap(policyBody?.conditions?.users?.excludeGroups)
  const includeLocationsMap = buildNamedLocationRequirementOptionsMap(
    policyBody?.conditions?.locations?.includeLocations,
    namedLocationDefinitions,
  )
  const excludeLocationsMap = buildNamedLocationRequirementOptionsMap(
    policyBody?.conditions?.locations?.excludeLocations,
    namedLocationDefinitions,
  )
  const includeApplicationsMap = buildApplicationRequirementOptionsMap(
    policyBody?.conditions?.applications?.includeApplications,
  )
  const excludeApplicationsMap = buildApplicationRequirementOptionsMap(
    policyBody?.conditions?.applications?.excludeApplications,
  )
  const includeUsersMap = buildUserRequirementOptionsMap(policyBody?.conditions?.users?.includeUsers)
  const excludeUsersMap = buildUserRequirementOptionsMap(policyBody?.conditions?.users?.excludeUsers)
  const includeRolesMap = buildDirectoryRoleRequirementOptionsMap(policyBody?.conditions?.users?.includeRoles)
  const excludeRolesMap = buildDirectoryRoleRequirementOptionsMap(policyBody?.conditions?.users?.excludeRoles)
  const includeServicePrincipalsMap = buildServicePrincipalRequirementOptionsMap(
    policyBody?.conditions?.clientApplications?.includeServicePrincipals,
  )
  const excludeServicePrincipalsMap = buildServicePrincipalRequirementOptionsMap(
    policyBody?.conditions?.clientApplications?.excludeServicePrincipals,
  )

  return {
    ...globalFormOptionsMap,
    includeGroups: { ...globalFormOptionsMap?.includeGroups, ...includeGroupsMap },
    excludeGroups: { ...globalFormOptionsMap?.excludeGroups, ...excludeGroupsMap },
    includeLocations: { ...globalFormOptionsMap?.includeLocations, ...includeLocationsMap },
    excludeLocations: { ...globalFormOptionsMap?.excludeLocations, ...excludeLocationsMap },
    includeApplications: {
      ...globalFormOptionsMap?.includeApplications,
      ...applicationNames,
      ...includeApplicationsMap,
    },
    excludeApplications: {
      ...globalFormOptionsMap?.excludeApplications,
      ...applicationNames,
      ...excludeApplicationsMap,
    },
    includeUsers: { ...globalFormOptionsMap?.includeUsers, ...includeUsersMap },
    excludeUsers: { ...globalFormOptionsMap?.excludeUsers, ...excludeUsersMap },
    includeRoles: { ...globalFormOptionsMap?.includeRoles, ...includeRolesMap },
    excludeRoles: { ...globalFormOptionsMap?.excludeRoles, ...excludeRolesMap },
    includeServicePrincipals: { ...globalFormOptionsMap?.includeServicePrincipals, ...includeServicePrincipalsMap },
    excludeServicePrincipals: { ...globalFormOptionsMap?.excludeServicePrincipals, ...excludeServicePrincipalsMap },
    customAuthenticationFactors: {
      ...globalFormOptionsMap?.customAuthenticationFactors,
      ...buildCustomAuthenticationFactorRequirementOptionsMap(policyBody?.grantControls?.customAuthenticationFactors),
    },
  }
}

function SelectedTemplatesView(props) {
  const { selectedTemplates = [], globalFormOptionsMap = {} } = props ?? {}

  const tabs = useMemo(
    () =>
      selectedTemplates.map((template) => ({
        id: template?.id,
        label: template?.template_name,
        content: (
          <ReviewTemplatePanel
            key={template?.id}
            templateDescription={template?.template_description}
            rawPolicy={template?.meta_data?.policyBody}
            promptedInputs={getDisplayPromptedInputs(template)}
            globalFormOptionsMap={buildTemplateFormOptionsMap(template, globalFormOptionsMap)}
            hideName
            headlessDetails
          />
        ),
      })),
    [selectedTemplates, globalFormOptionsMap],
  )

  if (!selectedTemplates?.length) {
    return null
  }

  return (
    <>
      <div className="text-strong mb-4">
        <FormattedMessage id="POLICY_GROUP.POLICY_DETAILS" />
      </div>
      <div className="text-color-secondary text-bold mb-4">
        <FormattedMessage id="POLICY_GROUP.SELECTED_TEMPLATES" />
      </div>

      <Accordion appearance="clean" showArrow arrowPosition="start" highlightOpenedTab defaultCollapsed tabs={tabs} />
    </>
  )
}

SelectedTemplatesView.propTypes = propTypes

export default SelectedTemplatesView
